import React, { forwardRef, ButtonHTMLAttributes } from 'react';
import Spinner from './Spinner';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  tipo?: 'default' | 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'outline-primary' | 'ghost';
  isLoading?: boolean;
  className?: string;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, tipo = 'default', onClick, disabled, isLoading = false, className, type = 'button', ...rest }, ref) => {
    const baseClasses = `
      relative
      inline-flex
      items-center
      justify-center
      px-6
      py-3
      font-semibold
      text-base
      rounded-lg
      transition-all
      duration-300
      ease-in-out
      focus:outline-none
      focus:ring-2
      focus:ring-offset-2
      focus:ring-offset-background
      whitespace-nowrap
      ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-[1.02] active:scale-[0.98]'}
    `;

    const variantClasses = {
      primary: `
        bg-primary
        text-background
        shadow-lg
        shadow-primary/30
        hover:bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663]
        focus:ring-primary
      `,
      secondary: `
        bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663]
        text-background
        shadow-md
        shadow-secondary/20
        hover:bg-primary
        focus:ring-secondary
      `,
      danger: `
        bg-danger
        text-white
        shadow-lg
        shadow-danger/30
        hover:bg-red-600
        focus:ring-danger
      `,
      success: `
        bg-success
        text-background
        shadow-lg
        shadow-success/30
        hover:bg-green-600
        focus:ring-success
      `,
      warning: `
        bg-warning
        text-background
        shadow-lg
        shadow-warning/30
        hover:bg-yellow-600
        focus:ring-warning
      `,
      'outline-primary': `
        border-2
        border-primary
        text-primary
        hover:bg-primary
        hover:text-background
        focus:ring-primary
      `,
      ghost: `
        text-primary
        hover:bg-primary/10
        focus:ring-primary
      `,
      default: `
        text-text
        shadow-md
        shadow-gray-700/20
        focus:ring-gray-500
      `,
    };

    const getSpinnerColor = (buttonTipo: ButtonProps['tipo']) => {
      switch (buttonTipo) {
        case 'primary':
        case 'secondary':
        case 'success':
        case 'warning':
          return 'text-background';
        case 'danger':
        case 'default':
        case 'outline-primary':
        case 'ghost':
        default:
          return 'text-text';
      }
    };

    const currentSpinnerColorClass = getSpinnerColor(tipo);

    const combinedClasses = `${baseClasses} ${variantClasses[tipo]} ${className || ''}`;

    return (
      <button
        ref={ref}
        onClick={onClick}
        disabled={disabled || isLoading}
        className={combinedClasses}
        type={type}
        {...rest}
      >
        {isLoading ? (
          <div className="flex items-center justify-center gap-2">
            <Spinner size="sm" color={currentSpinnerColorClass} />
            <span>Carregando...</span>
          </div>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;